import { useRef, useState } from 'react';
import { can } from '../auth/store';
import { ConfirmDialog } from './ui';
import {
  downloadComplianceDocument,
  useDeleteComplianceDocument,
  useUploadComplianceDocument,
  type DocumentSegment,
} from '../api/queries/compliance';

const ACCEPT = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp';

// Upload / replace / download / remove for one compliance entity's vault
// document. Renders inline; permission-gates writes on compliance.write.
export function DocumentAttachment({
  segment,
  id,
  documentPath,
}: {
  segment: DocumentSegment;
  id: number;
  documentPath: string | null;
}): JSX.Element {
  const canWrite = can('compliance.write');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const upload = useUploadComplianceDocument(segment);
  const remove = useDeleteComplianceDocument(segment);

  const onPick = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({ id, file });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <span className="document-attachment">
      {documentPath ? (
        <>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={upload.isPending || remove.isPending}
            onClick={() => {
              setError(null);
              void downloadComplianceDocument(segment, id, documentPath).catch((e: unknown) =>
                setError(e instanceof Error ? e.message : 'Download failed'));
            }}
          >
            Download
          </button>
          {canWrite && (
            <>
              {' · '}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={upload.isPending || remove.isPending}
                onClick={() => inputRef.current?.click()}
              >
                {upload.isPending ? 'Uploading…' : 'Replace'}
              </button>
              {' · '}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={upload.isPending || remove.isPending}
                onClick={() => setConfirmRemove(true)}
              >
                Remove
              </button>
            </>
          )}
        </>
      ) : canWrite ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending ? 'Uploading…' : 'Upload document'}
        </button>
      ) : (
        <span>—</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => void onPick(e.target.files?.[0])}
      />
      <ConfirmDialog
        open={confirmRemove}
        title="Remove document"
        message="Permanently delete this document? This cannot be undone."
        confirmLabel="Remove"
        danger
        busy={remove.isPending}
        onConfirm={() => {
          setError(null);
          remove.mutate(id, {
            onError: (e) => setError(e.message),
            onSettled: () => setConfirmRemove(false),
          });
        }}
        onCancel={() => setConfirmRemove(false)}
      />
      {error && <span className="error-text"> {error}</span>}
    </span>
  );
}
